/**
 * Enhanced Auto-Translate v2.5
 * RTL text support without affecting layout
 * Fixed: Always translate from stored English originals
 * Fixed: Selector collision - now uses unique runtime IDs for element tracking
 */
document.addEventListener("alpine:init", () => {
  Alpine.data("autoTranslate", () => ({
    currentLanguage: siteStorage.get("selected_language") || "en",
    isTranslating: false,
    originalsKey: "translation_originals",
    observer: null,
    translationTimeout: null,
    uidCounter: 0,

    rtlLanguages: ["ar", "he", "fa", "ur", "yi", "ps", "sd"],

    translatableAttributes: [
      "placeholder",
      "title",
      "alt",
      "aria-label",
      "aria-placeholder",
      "aria-description",
      "data-tooltip",
      "data-title",
      "data-placeholder",
      "data-error-message",
      "data-success-message",
      "data-empty-message",
      "data-loading-text",
      "data-confirm-text",
      "data-hint",
    ],

    init() {
      this.injectRTLStyles();

      // Clear stored originals on page load - server always sends fresh English HTML
      siteStorage.remove(this.originalsKey);

      // Also clear any existing UIDs from previous sessions
      document.querySelectorAll("[data-translate-uid]").forEach((el) => {
        el.removeAttribute("data-translate-uid");
      });

      window.addEventListener("language-changed", (e) => {
        this.switchLanguage(e.detail.language);
      });

      window.translateElement = (element) =>
        this.translateSingleElement(element);
      window.translateText = (text, targetLang) =>
        this.translateText(text, targetLang);

      if (this.currentLanguage !== "en") {
        this.translatePage(this.currentLanguage);
      }

      this.setTextDirection(this.currentLanguage);
      this.startObserver();

      document.body.addEventListener("htmx:afterSwap", (e) => {
        if (this.currentLanguage !== "en") {
          this.translateNewContent(e.detail.target);
        }
      });

      document.body.addEventListener("modal:opened", (e) => {
        if (this.currentLanguage !== "en") {
          const modal =
            e.detail?.modal || document.querySelector(".modal-container");
          setTimeout(() => this.translateNewContent(modal), 100);
        }
      });

      document.addEventListener("alpine:initialized", () => {
        if (this.currentLanguage !== "en") {
          this.translatePage(this.currentLanguage);
        }
      });
    },

    /**
     * Generate a unique ID for element tracking
     */
    generateUID() {
      return `tr_${Date.now()}_${++this.uidCounter}`;
    },

    /**
     * Get or create a unique ID for an element
     */
    getElementUID(element) {
      if (!element.dataset.translateUid) {
        element.dataset.translateUid = this.generateUID();
      }
      return element.dataset.translateUid;
    },

    loadOriginals() {
      try {
        const stored = siteStorage.get(this.originalsKey);
        return stored ? JSON.parse(stored) : {};
      } catch (e) {
        return {};
      }
    },

    saveOriginals(originals) {
      try {
        siteStorage.set(this.originalsKey, JSON.stringify(originals));
      } catch (e) {
        // Silent fail
      }
    },

    getCacheKey(text, targetLang, sourceLang = "en") {
      return `${sourceLang}:${targetLang}:${text}`;
    },

    injectRTLStyles() {
      if (document.getElementById("auto-translate-rtl-styles")) return;

      const style = document.createElement("style");
      style.id = "auto-translate-rtl-styles";
      style.textContent = `
        html[data-text-dir="rtl"] [data-translate],
        html[data-text-dir="rtl"] [data-translate] *:not(img):not(svg):not(i):not(button):not(a) {
          direction: rtl;
          text-align: right;
          unicode-bidi: plaintext;
        }

        html[data-text-dir="rtl"] i,
        html[data-text-dir="rtl"] svg,
        html[data-text-dir="rtl"] img,
        html[data-text-dir="rtl"] .fa,
        html[data-text-dir="rtl"] .fas,
        html[data-text-dir="rtl"] .far,
        html[data-text-dir="rtl"] .fab {
          direction: ltr;
        }

        html[data-text-dir="rtl"] nav,
        html[data-text-dir="rtl"] header,
        html[data-text-dir="rtl"] footer,
        html[data-text-dir="rtl"] .container,
        html[data-text-dir="rtl"] .flex,
        html[data-text-dir="rtl"] .grid {
          direction: ltr;
        }

        html[data-text-dir="rtl"] input[data-translate]::placeholder,
        html[data-text-dir="rtl"] textarea[data-translate]::placeholder {
          direction: rtl;
          text-align: right;
        }

        html[data-text-dir="rtl"] input[data-translate],
        html[data-text-dir="rtl"] textarea[data-translate] {
          direction: rtl;
          text-align: right;
        }

        html[data-text-dir="rtl"] button,
        html[data-text-dir="rtl"] a {
          direction: ltr;
        }

        html[data-text-dir="rtl"] button[data-translate] > span,
        html[data-text-dir="rtl"] button[data-translate] > p,
        html[data-text-dir="rtl"] a[data-translate] > span,
        html[data-text-dir="rtl"] a[data-translate] > p,
        html[data-text-dir="rtl"] [data-translate] span,
        html[data-text-dir="rtl"] [data-translate] p,
        html[data-text-dir="rtl"] [data-translate] h1,
        html[data-text-dir="rtl"] [data-translate] h2,
        html[data-text-dir="rtl"] [data-translate] h3,
        html[data-text-dir="rtl"] [data-translate] h4,
        html[data-text-dir="rtl"] [data-translate] h5,
        html[data-text-dir="rtl"] [data-translate] h6 {
          direction: rtl;
          unicode-bidi: plaintext;
        }

        html[data-text-dir="rtl"] [data-translate].block,
        html[data-text-dir="rtl"] .dropdown [data-translate] {
          direction: rtl;
          text-align: right;
        }

        html[data-text-dir="rtl"] select[data-translate],
        html[data-text-dir="rtl"] select[data-translate] option {
          direction: rtl;
          text-align: right;
        }
      `;
      document.head.appendChild(style);
    },

    async switchLanguage(langCode) {
      // Skip if already on this language
      if (this.currentLanguage === langCode) return;

      this.currentLanguage = langCode;
      siteStorage.set("selected_language", langCode);
      this.setTextDirection(langCode);

      window.dispatchEvent(
        new CustomEvent("language-switching", {
          detail: { language: langCode },
        }),
      );

      // ALWAYS restore to English first, then translate if needed
      this.restoreOriginalContent();

      if (langCode !== "en") {
        await this.translatePage(langCode);
      }

      window.dispatchEvent(
        new CustomEvent("language-switched", {
          detail: { language: langCode },
        }),
      );
    },

    setTextDirection(langCode) {
      const isRTL = this.rtlLanguages.includes(langCode);
      document.documentElement.setAttribute(
        "data-text-dir",
        isRTL ? "rtl" : "ltr",
      );
      document.documentElement.setAttribute("lang", langCode);
      document.body.classList.toggle("text-rtl", isRTL);
      document.body.classList.toggle("text-ltr", !isRTL);
      document.documentElement.setAttribute("dir", "ltr");
    },

    async translateText(text, targetLang) {
      try {
        const translations = await window.translationService.translateBatch(
          [text],
          targetLang,
          "en",
        );
        return translations[0];
      } catch (error) {
        return text;
      }
    },

    async translateNewContent(container) {
      if (!container || this.currentLanguage === "en") return;

      const elements = container.querySelectorAll("[data-translate]");
      if (elements.length === 0) return;

      await this.translateElements(elements, this.currentLanguage);
    },

    async translateSingleElement(element) {
      if (!element || this.currentLanguage === "en") return;
      await this.translateElements([element], this.currentLanguage);
    },

    async translatePage(targetLang) {
      if (this.isTranslating) {
        return;
      }

      const elements = document.querySelectorAll("[data-translate]");
      if (elements.length === 0) return;

      await this.translateElements(elements, targetLang);
    },

    async translateElements(elements, targetLang) {
      this.isTranslating = true;

      const originals = this.loadOriginals();
      const textsToTranslate = [];
      const translationItems = [];

      elements.forEach((element) => {
        // Get or create unique ID for this element
        const uid = this.getElementUID(element);
        const detectedItems = this.detectAllTranslatableContent(element, uid);

        detectedItems.forEach((item) => {
          const storageKey = item.storageKey;

          // Check if we have stored original for this element
          if (originals[storageKey]) {
            // USE stored English original, not current DOM content
            item.originalText = originals[storageKey].text;
          } else {
            // First time seeing this element - store its current text as original
            if (!item.text || !item.text.trim()) return;

            originals[storageKey] = {
              text: item.text.trim(),
              uid: uid,
              type: item.type,
              attribute: item.attribute,
              property: item.property,
              optionIndex: item.optionIndex,
            };
            item.originalText = item.text.trim();
          }

          // Check TranslationService cache using ENGLISH original
          const cacheKey = this.getCacheKey(
            item.originalText,
            targetLang,
            "en",
          );
          const cached = window.translationService.cache[cacheKey];

          if (cached) {
            this.applyTranslation(item, cached);
          } else {
            textsToTranslate.push(item.originalText);
            translationItems.push(item);
          }
        });
      });

      this.saveOriginals(originals);

      if (textsToTranslate.length === 0) {
        this.isTranslating = false;
        return;
      }

      const uniqueTexts = [...new Set(textsToTranslate)];
      const textToIndexMap = new Map();
      uniqueTexts.forEach((text, idx) => textToIndexMap.set(text, idx));

      try {
        // Always translate from English
        const translations = await window.translationService.translateBatch(
          uniqueTexts,
          targetLang,
          "en",
        );

        translationItems.forEach((item) => {
          const textIndex = textToIndexMap.get(item.originalText);
          const translatedText = translations[textIndex];
          this.applyTranslation(item, translatedText);
        });
      } catch (error) {
        // Silent fail - keep current text
      }

      this.isTranslating = false;
    },

    detectAllTranslatableContent(element, uid) {
      const items = [];

      const alpineItem = this.detectAlpineBinding(element, uid);
      if (alpineItem) items.push(alpineItem);

      this.translatableAttributes.forEach((attr) => {
        const attrItem = this.detectAttribute(element, attr, uid);
        if (attrItem) items.push(attrItem);
      });

      const valueItem = this.detectValueAttribute(element, uid);
      if (valueItem) items.push(valueItem);

      if (element.tagName === "SELECT") {
        const optionItems = this.detectSelectOptions(element, uid);
        items.push(...optionItems);
      }

      if (!alpineItem && this.hasDirectTextContent(element)) {
        const textItem = this.detectTextContent(element, uid);
        if (textItem) items.push(textItem);
      }

      return items;
    },

    detectAlpineBinding(element, uid) {
      const xTextAttr = element.getAttribute("x-text");
      const xHtmlAttr = element.getAttribute("x-html");
      const alpineExpression = xTextAttr || xHtmlAttr;

      if (!alpineExpression) return null;

      const alpineEl = element.closest("[x-data]");
      if (!alpineEl) return null;

      let alpineData;
      try {
        alpineData = Alpine.$data(alpineEl);
      } catch (e) {
        return null;
      }

      if (!alpineData) return null;

      const propertyValue = this.getNestedValue(alpineData, alpineExpression);

      if (propertyValue === undefined || propertyValue === null) return null;
      if (typeof propertyValue !== "string") return null;

      // Use UID for unique storage key
      const storageKey = `${uid}:alpine:${alpineExpression}`;

      return {
        element: alpineEl,
        targetElement: element,
        type: "alpine",
        property: alpineExpression,
        isHtml: !!xHtmlAttr,
        text: propertyValue,
        uid,
        storageKey,
      };
    },

    getNestedValue(obj, path) {
      if (!path.includes(".")) {
        return obj[path];
      }

      return path.split(".").reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
      }, obj);
    },

    setNestedValue(obj, path, value) {
      if (!path.includes(".")) {
        obj[path] = value;
        return;
      }

      const keys = path.split(".");
      const lastKey = keys.pop();
      const target = keys.reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
      }, obj);

      if (target && lastKey) {
        target[lastKey] = value;
      }
    },

    detectAttribute(element, attrName, uid) {
      const attrValue = element.getAttribute(attrName);
      if (!attrValue || !attrValue.trim()) return null;

      // Use UID for unique storage key
      const storageKey = `${uid}:attr:${attrName}`;

      return {
        element,
        type: "attribute",
        attribute: attrName,
        text: attrValue,
        uid,
        storageKey,
      };
    },

    detectValueAttribute(element, uid) {
      const isButton =
        element.tagName === "BUTTON" ||
        (element.tagName === "INPUT" &&
          ["submit", "button", "reset"].includes(element.type));

      if (!isButton || !element.value || !element.value.trim()) return null;

      // Use UID for unique storage key
      const storageKey = `${uid}:value`;

      return {
        element,
        type: "value",
        text: element.value,
        uid,
        storageKey,
      };
    },

    detectSelectOptions(selectElement, uid) {
      const items = [];

      Array.from(selectElement.options).forEach((option, index) => {
        if (!option.text || !option.text.trim()) return;

        // Use UID + index for unique storage key
        const storageKey = `${uid}:option:${index}`;

        items.push({
          element: option,
          type: "option",
          text: option.text,
          optionIndex: index,
          uid,
          storageKey,
        });
      });

      return items;
    },

    hasDirectTextContent(element) {
      const skipTags = [
        "SCRIPT",
        "STYLE",
        "SVG",
        "IMG",
        "INPUT",
        "TEXTAREA",
        "SELECT",
        "IFRAME",
      ];
      if (skipTags.includes(element.tagName)) return false;

      for (const node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          return true;
        }
      }

      if (element.children.length === 0 && element.textContent.trim()) {
        return true;
      }

      return false;
    },

    detectTextContent(element, uid) {
      let directText = "";
      for (const node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          directText += node.textContent;
        }
      }

      if (!directText.trim() && element.children.length === 0) {
        directText = element.textContent;
      }

      if (!directText.trim()) return null;

      // Use UID for unique storage key
      const storageKey = `${uid}:text`;

      return {
        element,
        type: "text",
        text: directText,
        uid,
        storageKey,
      };
    },

    applyTranslation(translationData, translatedText) {
      const { element, type, property, attribute, isHtml } = translationData;

      if (!document.contains(element)) return;

      try {
        switch (type) {
          case "alpine":
            const alpineData = Alpine.$data(translationData.element);
            if (alpineData && property) {
              this.setNestedValue(alpineData, property, translatedText);
            }
            break;

          case "attribute":
            element.setAttribute(attribute, translatedText);
            break;

          case "value":
            element.value = translatedText;
            break;

          case "option":
            element.text = translatedText;
            break;

          case "text":
          default:
            this.replaceTextNodes(element, translatedText);
            break;
        }
      } catch (error) {
        // Silent fail
      }
    },

    replaceTextNodes(element, newText) {
      if (element.children.length === 0) {
        element.textContent = newText;
        return;
      }

      let replaced = false;
      for (const node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          if (!replaced) {
            node.textContent = newText;
            replaced = true;
          } else {
            node.textContent = "";
          }
        }
      }
    },

    restoreOriginalContent() {
      const originals = this.loadOriginals();
      if (Object.keys(originals).length === 0) return;

      Object.entries(originals).forEach(([storageKey, stored]) => {
        if (!stored || !stored.text || !stored.uid) return;

        // Find element by its unique ID
        const element = document.querySelector(
          `[data-translate-uid="${stored.uid}"]`,
        );
        if (!element) return;

        try {
          switch (stored.type) {
            case "alpine":
              const alpineData = Alpine.$data(element);
              if (alpineData && stored.property) {
                this.setNestedValue(alpineData, stored.property, stored.text);
              }
              break;

            case "attribute":
              if (stored.attribute) {
                element.setAttribute(stored.attribute, stored.text);
              }
              break;

            case "value":
              element.value = stored.text;
              break;

            case "option":
              if (
                typeof stored.optionIndex === "number" &&
                element.options &&
                element.options[stored.optionIndex]
              ) {
                element.options[stored.optionIndex].text = stored.text;
              }
              break;

            case "text":
            default:
              this.replaceTextNodes(element, stored.text);
              break;
          }
        } catch (error) {
          // Silent fail
        }
      });
    },

    startObserver() {
      if (this.observer) return;

      this.observer = new MutationObserver((mutations) => {
        if (this.currentLanguage === "en") return;

        let newElements = [];

        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;

            if (node.hasAttribute && node.hasAttribute("data-translate")) {
              newElements.push(node);
            }

            if (node.querySelectorAll) {
              const translatableElements =
                node.querySelectorAll("[data-translate]");
              newElements.push(...translatableElements);
            }
          });
        });

        if (newElements.length > 0) {
          clearTimeout(this.translationTimeout);
          this.translationTimeout = setTimeout(() => {
            this.translateElements(newElements, this.currentLanguage);
          }, 150);
        }
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    },

    stopObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },

    clearCache() {
      siteStorage.remove(this.originalsKey);
      // Clear UIDs from elements
      document.querySelectorAll("[data-translate-uid]").forEach((el) => {
        el.removeAttribute("data-translate-uid");
      });
      if (window.translationService) {
        window.translationService.clearCache();
      }
    },

    getStats() {
      const originals = this.loadOriginals();
      return {
        trackedElements: Object.keys(originals).length,
        currentLanguage: this.currentLanguage,
        isTranslating: this.isTranslating,
        uidCounter: this.uidCounter,
      };
    },
  }));
});
